import {
  emptyCell,
  FilterDefs,
  GridCellContent,
  GridColumn,
  HasIdAndName,
  multiFilter,
  numericColumn,
  SimpleHeaderAndData,
} from "@homebound/beam";
import { formatToPrice, percentageCell, priceCell } from "src/components";
import {
  CostConfigurationSummariesFilter,
  CostConfigurationSummaryOrderBy,
  PotentialBidPlanDimensionKind,
} from "src/generated/graphqlTypes";
import { formatNumberToString } from "src/utils";
import {
  AllConfigurations_CostConfigurationSummaryFragment,
  AllConfigurations_PotentialBidPlanDimensionResultFragment,
} from "./AllConfigurationsTab.generated";
import {
  ConfigurationComparison_CombinationFragment,
  ConfigurationComparison_CostConfigurationSummaryFragment,
  ConfigurationComparison_PotentialBidPlanDimensionResultFragment,
  ConfigurationComparison_ReadyPlanOptionFragment,
} from "./ConfigurationComparisonTab.generated";

export const ALL_CONFIGURATIONS_PAGE_SIZE = 200;
export const COMPARISON_COMBINATIONS_LIMIT = 10_000;
export const COMPARISON_PAGE_SIZE = 1_000;

/** The comparison only filters by offering; its dimensions are fixed by the selects above the table. */
export type ComparisonFilter = { productOffering?: string[] };
/** The offering filter plus one selection per dimension, keyed by the dimension's GlobalOptionType id. */
export type AllConfigurationsFilter = ComparisonFilter & Record<string, string[] | undefined>;

type Dimension = { kind: PotentialBidPlanDimensionKind; type?: { id: string } | null };
type TypeDimension<D extends Dimension> = D & { type: NonNullable<D["type"]> };
export type ComparisonDimension = TypeDimension<ConfigurationComparison_PotentialBidPlanDimensionResultFragment>;
type ValueGroup = HasIdAndName & { ids: string[] };

/** The dimensions backed by a GlobalOptionType, which drive the table's columns and filters. */
export function typeDimensions<D extends Dimension>(dimensions: D[]): TypeDimension<D>[] {
  return dimensions.filter(
    (d): d is TypeDimension<D> => d.kind === PotentialBidPlanDimensionKind.GlobalOptionType && !!d.type,
  );
}

/** Groups options that share a name into one value whose id is their comma-joined ids, so selecting it selects them all. */
function groupOptionsByName(options: HasIdAndName[]): ValueGroup[] {
  return options
    .groupByObject(
      (o) => o.name,
      (o) => o.id,
    )
    .map(([name, ids]) => ({ id: ids.join(","), name, ids }));
}

export function createOfferingFilterDefs(productOfferings: HasIdAndName[]): FilterDefs<ComparisonFilter> {
  return { productOffering: namedFilter("Product Offering", productOfferings) };
}

/** One multi-select per dimension, whose values are GlobalOption ids since those are shared across offerings. */
export function createFilterDefs(
  productOfferings: HasIdAndName[],
  dimensions: AllConfigurations_PotentialBidPlanDimensionResultFragment[],
): FilterDefs<AllConfigurationsFilter> {
  return {
    ...createOfferingFilterDefs(productOfferings),
    ...typeDimensions(dimensions).mapToObject((d) => [
      d.type.id,
      namedFilter(d.displayName, groupOptionsByName(d.options)),
    ]),
  };
}

function namedFilter(label: string, options: HasIdAndName[]) {
  return multiFilter({ label, options, getOptionValue: (o) => o.id, getOptionLabel: (o) => o.name });
}

/** Maps the table's filter to the query's filter, where an unselected filter means every value. */
export function mapToFilter(
  filter: AllConfigurationsFilter,
  searchString: string | undefined,
  activeProductOfferingIds: string[],
): CostConfigurationSummariesFilter {
  const { productOffering, ...dimensions } = filter;
  const selected = Object.values(dimensions)
    .compact()
    .filter((globalOptions) => globalOptions.nonEmpty);
  return {
    productOfferings: selectedOfferingIds(productOffering, activeProductOfferingIds),
    ...(selected.nonEmpty && {
      dimensions: selected.map((globalOptions) => ({ globalOptions: globalOptions.flatMap((o) => o.split(",")) })),
    }),
    ...(searchString && { search: searchString }),
  };
}

/** No selected offering means every active one, which keeps archived and draft offerings out of the table. */
export function selectedOfferingIds(selected: string[] | undefined, activeProductOfferingIds: string[]): string[] {
  return selected?.nonEmpty ? selected : activeProductOfferingIds;
}

type OfferingOption = ConfigurationComparison_ReadyPlanOptionFragment;
type Combination = ConfigurationComparison_CombinationFragment;
type Summary = ConfigurationComparison_CostConfigurationSummaryFragment;
/**
 * What the user picked; anything unpicked, or no longer available, resolves to a default. The baseline and the fixed
 * values are a value's id, or one of its option ids when linked from All Configurations.
 */
export type ComparisonPicks = {
  compareTypeId?: string;
  baselineId?: string;
  fixed: Record<string, string | undefined>;
};
type FixedDimension = { dimension: ComparisonDimension; options: ValueGroup[]; value: ValueGroup | undefined };
export type ResolvedSelection = {
  compare: ComparisonDimension;
  compareValues: ValueGroup[];
  baseline: ValueGroup | undefined;
  fixed: FixedDimension[];
};
/** A compared configuration alongside its offering's baseline, which the delta columns subtract. */
export type ComparisonRow = {
  id: string;
  summary: Summary;
  baseline: Summary;
  isBaseline: boolean;
  compareValue: string;
};

/**
 * The dimension's values that at least one combination pins, in checkout order. Offerings can order a value
 * differently, so the first offering (by name) that offers it decides.
 */
function allCombinationOptionIds(
  dimension: ComparisonDimension,
  offeringOptions: OfferingOption[],
  combinations: Combination[],
) {
  const pinned = combinations.flatMap((c) => c.pinnedGlobalOptionIds).unique();
  const options = offeringOptions.sortBy((rpo) => rpo.readyPlan.name);
  return groupOptionsByName(dimension.options)
    .filter((group) => group.ids.some((id) => pinned.includes(id)))
    .sortBy((group) => options.find((rpo) => group.ids.includes(rpo.globalOption.id))?.order ?? Infinity);
}

/** The dimension the comparison opens on: the spec level, else the last one. */
export function defaultCompareDimension<D extends { type: { isSpecLevel: boolean } }>(dimensions: D[]): D | undefined {
  return dimensions.find((d) => d.type.isSpecLevel) ?? dimensions.last;
}

/**
 * Fills in the compare dimension, its baseline and the fixed values from the user's picks: the spec level, then each
 * dimension's first value in checkout order. Fixed dimensions resolve left to right against the combinations that match
 * the baseline and the values fixed so far, so a pick that's no longer available falls back to the first available. A
 * compare dimension without values has no baseline, so nothing is available to fix either.
 */
export function resolveSelection(
  picks: ComparisonPicks,
  dimensions: ComparisonDimension[],
  offeringOptions: OfferingOption[],
  combinations: Combination[],
): ResolvedSelection | undefined {
  const dimensionToCompare =
    dimensions.find((d) => d.type.id === picks.compareTypeId) ?? defaultCompareDimension(dimensions);
  if (!dimensionToCompare) return undefined;
  const compareValues = allCombinationOptionIds(dimensionToCompare, offeringOptions, combinations);
  const baseline = pickValue(compareValues, picks.baselineId);
  const fixed: FixedDimension[] = [];
  let matching = combinations.filter((c) => baseline?.ids.some((id) => c.pinnedGlobalOptionIds.includes(id)));
  for (const dimension of dimensions.filter((d) => d !== dimensionToCompare)) {
    const options = allCombinationOptionIds(dimension, offeringOptions, matching);
    const value = pickValue(options, picks.fixed[dimension.type.id]);
    if (value) matching = matching.filter((c) => value.ids.some((id) => c.pinnedGlobalOptionIds.includes(id)));
    fixed.push({ dimension, options, value });
  }
  return { compare: dimensionToCompare, compareValues, baseline, fixed };
}

/** The value picked by its id or by one of its option ids, else the first. */
function pickValue(values: ValueGroup[], pick: string | undefined): ValueGroup | undefined {
  return values.find((v) => v.id === pick || (!!pick && v.ids.includes(pick))) ?? values.first;
}

/** The query filter for the compared rows: the selected offerings, pinned to every fixed value. */
export function toComparisonFilter(
  { fixed }: ResolvedSelection,
  offeringFilter: string[] | undefined,
  activeProductOfferingIds: string[],
): CostConfigurationSummariesFilter {
  return mapToFilter(
    { productOffering: offeringFilter, ...fixed.mapToObject((f) => [f.dimension.type.id, f.value?.ids]) },
    undefined,
    activeProductOfferingIds,
  );
}

/**
 * Groups the configurations by offering, the baseline first then the rest in checkout order, and drops offerings that
 * don't offer the baseline since their rows would have nothing to compare against.
 */
export function createComparisonRows(
  summaries: Summary[],
  { compare, compareValues, baseline }: ResolvedSelection,
): ComparisonRow[] {
  function compareOption(summary: Summary) {
    return summary.pinnedOptions.find((rpo) => rpo.type.id === compare.type.id);
  }
  function valueIndex(summary: Summary) {
    const index = compareValues.findIndex((v) => v.ids.includes(compareOption(summary)?.globalOption.id ?? ""));
    return index === -1 ? compareValues.length : index;
  }
  return Object.values(summaries.groupBy((s) => s.productOffering.id))
    .sortBy((group) => group[0].productOffering.name)
    .flatMap((group) => {
      const baselineSummary = group.find((s) => baseline?.ids.includes(compareOption(s)?.globalOption.id ?? ""));
      if (!baselineSummary) return [];
      return [baselineSummary, ...group.filter((s) => s !== baselineSummary).sortBy(valueIndex)].map((summary) => ({
        id: summary.id,
        summary,
        baseline: baselineSummary,
        isBaseline: summary === baselineSummary,
        compareValue: compareOption(summary)?.name ?? "",
      }));
    });
}

/** The percent `value` is above (positive) or below `baseline`, or undefined without a baseline to compare against. */
export function deltaPercent(value: number, baseline: number): number | undefined {
  return baseline === 0 ? undefined : ((value - baseline) / baseline) * 100;
}

/** A signed amount, e.g. "+$8,418.18". */
export function deltaPriceCell(valueInCents: number): GridCellContent {
  return { content: formatToPrice({ valueInCents, displayDirection: true }), value: valueInCents };
}

/** A signed percent to one decimal, e.g. "+15.3%", or empty without a baseline to measure against. */
export function deltaPercentageCell(percent: number | null | undefined): GridCellContent {
  if (percent === undefined || percent === null) return emptyCell;
  return { content: `${percent > 0 ? "+" : ""}${formatNumberToString(percent, false, 1)}%`, value: percent };
}

type MetricSummary = Summary | AllConfigurations_CostConfigurationSummaryFragment;
type MetricKey = Exclude<keyof CostConfigurationSummaryOrderBy, "productOffering">;

/**
 * The hideable cost columns both tables show, reading the summary off each row. All Configurations sorts them on the
 * server by their key; the comparison keeps its baseline-first row order, so its columns don't sort. The cost breakdown
 * columns' info icon opens the Cost Category definitions.
 */
export function createMetricColumns<D>(
  summaryOf: (data: D) => MetricSummary,
  sortable: boolean,
  openDefinitions: VoidFunction,
): GridColumn<SimpleHeaderAndData<D>>[] {
  function metric(
    id: MetricKey,
    name: string,
    cell: (s: MetricSummary) => GridCellContent,
    opts: { initHidden?: boolean; tooltip?: VoidFunction } = {},
  ) {
    return numericColumn<SimpleHeaderAndData<D>>({
      id,
      name,
      header: name,
      canHide: true,
      initHidden: true,
      mw: "120px",
      ...opts,
      data: (data) => cell(summaryOf(data)),
      ...(sortable ? { serverSideSortKey: id } : { clientSideSort: false }),
    });
  }
  return [
    metric("totalCostInCents", "Total Cost", (s) => priceCell({ valueInCents: s.totalCostInCents }), {
      initHidden: false,
    }),
    metric(
      "costPerSellableSqftInCents",
      "Cost / Sq Ft",
      (s) => priceCell({ valueInCents: s.costPerSellableSqftInCents }),
      { initHidden: false },
    ),
    metric("sellableSqft", "Sq. Ft.", (s) => ({
      content: s.programData.sellableSqft ? formatNumberToString(s.programData.sellableSqft, true) : "-",
    })),
    metric("totalPriceInCents", "Total Price", (s) => priceCell({ valueInCents: s.totalPriceInCents })),
    metric("pricePerSellableSqftInCents", "Price / Sq Ft", (s) =>
      priceCell({ valueInCents: s.pricePerSellableSqftInCents }),
    ),
    metric("indirectCostInCents", "Indirect Hard Costs", (s) => priceCell({ valueInCents: s.indirectCostInCents }), {
      tooltip: openDefinitions,
    }),
    metric("directCostInCents", "Direct Hard Costs", (s) => priceCell({ valueInCents: s.directCostInCents }), {
      tooltip: openDefinitions,
    }),
    metric(
      "provisionalCostsInCents",
      "Provisional Estimates $",
      (s) => priceCell({ valueInCents: s.provisionalCostsInCents }),
      { tooltip: openDefinitions },
    ),
    metric("provisionalPercentage", "Provisional Estimates %", (s) => percentageCell(s.provisionalPercentage), {
      tooltip: openDefinitions,
    }),
    metric("softCostInCents", "Soft Costs", (s) => priceCell({ valueInCents: s.softCostInCents }), {
      tooltip: openDefinitions,
    }),
    metric("contingencyMarkupPercentage", "Contingency %", (s) =>
      percentageCell(s.productOffering.contingencyMarkupPercentage),
    ),
    metric("contingencyInCents", "Contingency $", (s) => priceCell({ valueInCents: s.contingencyInCents })),
  ];
}
